import { AfterViewInit, Component, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { BlockNodeEnum, BlockTypeEnum } from '@app/core/enums';
import { FilterType, ViewPart } from '@app/core/models';
import { Block } from '@app/core/models/block/block.model';
import { GlobalService } from '@app/core/services/common/global.service';
import { FiltersService } from '@app/shared/services/filters/filters.service';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';

/** Placeholder pokazywany, gdy na liście nie obowiązuje żadne sortowanie z tabeli poniżej. */
const ETYKIETA_BEZ_SORTOWANIA = 'sorting';

/**
 * Pozycje sortowania rozpoznawane przez front, w rozbiciu na widoki list. Ustawienia bloku
 * decydują, które z nich redaktor udostępnił odwiedzającemu (`loadSortOptions`), ale etykietę
 * dla adresu czytamy z pełnej tabeli — inaczej po wejściu pod odnośnik z parametrem `sorting`
 * etykieta gubiłaby się do czasu, aż dojdą ustawienia z backendu.
 */
const SORT_OPTIONS_BY_VIEW: Record<string, { key: string; label: string; value: string }[]> = {
  inventoryWorksView: [
    {
      key: 'inventoriArtist',
      label: 'inventoriName.artistAtoZ',
      value: 'artist_asc',
    },
    {
      key: 'inventoriArtist',
      label: 'inventoriName.artistZtoA',
      value: 'artist_desc',
    },
    {
      key: 'inventoriTitle',
      label: 'inventoriName.titleAtoZ',
      value: 'title_asc',
    },
    {
      key: 'inventoriTitle',
      label: 'inventoriName.titleZtoA',
      value: 'title_desc',
    },
    {
      key: 'inventoriPrice',
      label: 'inventoriName.priceLowestFirst',
      value: 'price_asc',
    },
    {
      key: 'inventoriPrice',
      label: 'inventoriName.priceHighestfirst',
      value: 'price_desc',
    },
    {
      key: 'inventoriDateOfCreation',
      label: 'inventoriName.dateOfCreationOldestFirst',
      value: 'creation_date_asc',
    },
    {
      key: 'inventoriDateOfCreation',
      label: 'inventoriName.dateOfCreationYoungestFirst',
      value: 'creation_date_desc',
    },
    {
      key: 'inventoriDateAdded',
      label: 'inventoriName.dateAddedNewest',
      value: 'added_date_desc',
    },
    {
      key: 'inventoriDateAdded',
      label: 'inventoriName.dateAddedOldest',
      value: 'added_date_asc',
    },
  ],
  artistView: [
    {key: 'artistName', label: 'artist.nameAtoZ', value: 'lastname_asc'},
    {key: 'artistName', label: 'artist.nameZtoA', value: 'lastname_desc'},
    {
      key: 'atristDateFirst',
      label: 'artist.dateNewestFirst',
      value: 'date_desc',
    },
    {
      key: 'atristDateFirst',
      label: 'artist.dateOldestFirst',
      value: 'date_asc',
    },
  ],
  exhibitionsView: [
    {
      key: 'newsDateFirst',
      label: 'news.dateNewestFirst',
      value: 'date_desc',
    },
    {
      key: 'newsDateFirst',
      label: 'news.dateOldestFirst',
      value: 'date_asc',
    },
  ],
  news: [
    {
      key: 'newsDateFirst',
      label: 'news.dateNewestFirst',
      value: 'date_desc',
    },
    {
      key: 'newsDateFirst',
      label: 'news.dateOldestFirst',
      value: 'date_asc',
    },
  ],
  articlesView: [
    {
      key: 'newsDateFirst',
      label: 'news.dateNewestFirst',
      value: 'date_desc',
    },
    {
      key: 'newsDateFirst',
      label: 'news.dateOldestFirst',
      value: 'date_asc',
    },
  ],
};

@Component({
  selector: 'app-sorting-list',
  templateUrl: './sorting-list.component.html',
  styleUrls: ['./sorting-list.component.scss'],
  standalone: false,
})
export class SortingListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() specialBlock: Block;
  @Input() typBlock: BlockTypeEnum;
  @Input() viewPartBlock: string;
  @Input() keyBlock: string;
  @Input() stylsParentBlock: any;

  viewPartType: ViewPart;
  blockNodeEnum = BlockNodeEnum;

  sortingPositions: any;
  selectedOption = '';
  viewName = '';
  isHidden = true;

  sortOptions: { label: string; value: string }[] = [];

  private globalClickListener: () => void;
  private subscriptions: Subscription[] = [];

  /** Parametry adresu z ostatniej emisji — widok listy bywa znany dopiero po nich. */
  private queryParams: Params = {};

  constructor(
    private renderer: Renderer2,
    private globalService: GlobalService,
    private fs: FiltersService,
    private route: ActivatedRoute,
  ) {
    this.subscriptions.push(
      this.fs?.listFilterSlidable.subscribe((data) => {
        if (data?.[0]?.elements?.[0]?.viewName) {
          this.sortingPositions =
            data[0]?.sorting[0]?.specialBlock?.settings.sorting;
          this.viewName = data[0]?.elements[0]?.viewName;
          if (!this.sortingPositions) {
            this.sortingPositions = this.getSettingsSorting(data[0].header);
          }
          this.syncSelectedSortingWithUrl();
        }
      }),
    );

    // AT-9250: etykieta ma pokazywać sortowanie faktycznie obowiązujące na liście, a to
    // jedzie w adresie. Subskrypcja, a nie zrzut z chwili utworzenia, bo adres zmienia się
    // w trakcie życia komponentu: wybór sortowania, powrót do listy, wstecz/dalej.
    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        this.queryParams = params || {};
        this.syncSelectedSortingWithUrl();
      }),
    );
  }

  /**
   * Przepisuje sortowanie z adresu na etykietę widoku. Brak parametru znaczy, że lista
   * jedzie sortowaniem domyślnym bloku — wtedy pokazujemy placeholder, bo odwiedzający
   * niczego nie wybrał. Wartość spoza tabeli widoku (np. sortowanie artystów w adresie
   * listy prac) też daje placeholder, zamiast etykiety z cudzej listy.
   */
  private syncSelectedSortingWithUrl(): void {
    if (!this.viewName) {
      return;
    }

    const wartosc = this.queryParams[FilterType.SORTING];
    const wybrana = Array.isArray(wartosc) ? wartosc[wartosc.length - 1] : wartosc;
    const pozycja = (SORT_OPTIONS_BY_VIEW[this.viewName] || [])
      .find((option) => option.value === wybrana);

    this.globalService.optionSelect[this.viewName] = pozycja
      ? pozycja.label
      : ETYKIETA_BEZ_SORTOWANIA;
  }

  getSettingsSorting(data: any) {
    for (const e of data) {
      if (e.specialBlockType === BlockTypeEnum.SORTING_LIST_TYPE) {
        return e.specialBlock?.settings?.sorting;
      } else if (e.children && e.children.length > 0) {
        const result = this.getSettingsSorting(e.children);
        if (result) return result;
      }
    }
    return null;
  }

  ngOnInit(): void {
    this.loadSortOptions();
    setTimeout(() => {
      this.sortingPositionView(this.specialBlock);
    });
    this.globalClickListener = this.renderer.listen(
      'document',
      'click',
      (event) => {
        const target = event.target as HTMLElement;
        const closestSortingList = target.closest('app-sorting-list');
        if (!closestSortingList) {
          this.closeAllVisibleLists();
          return;
        }
        const isFirstElement =
          closestSortingList ===
          document.querySelector('app-sorting-list:first-of-type');
        if (!isFirstElement) {
          this.closeAllVisibleLists();
        }
      },
    );
  }

  private closeAllVisibleLists(): void {
    Object.keys(this.globalService.specialBlockUId).forEach((key) => {
      const elementClass = this.globalService.specialBlockUId[key];
      const element = document.querySelector(`.${elementClass}`) as HTMLElement;
      if (element && element.style.display === 'block') {
        element.style.display = 'none';
      }
    });
  }

  parseStyle(block) {
    // const viewPart = block?.settings?.viewPart;
    return block.uId;
  }

  sortingPositionView(specialBlock): void {
    const viewPart = specialBlock?.settings?.viewPart;
    if (viewPart === ViewPart.SORTING_POSITION) {
      const element = document.querySelector(
        `.${specialBlock.uId}`,
      ) as HTMLElement;
      if (!element) {
        return;
      }
      this.fs.parentBlock = specialBlock.uId;
      element.classList.add('reset-all-styles');
      element.style.display = 'none';
      element.style.top = `${this.fs.marginBottom}`;
      element.style.zIndex = '99999';
      element.style.border = 'none';
      element.style.padding = 'inherit';
      element.style.margin = 'inherit';
      element.style.position = 'absolute';
      element.style.backgroundColor = 'inherit';
      element.style.width = this.fs.sizeBlock;
      if (!this.globalService.specialBlockUId) {
        this.globalService.specialBlockUId = {};
      }
      this.globalService.specialBlockUId[this.keyBlock] = specialBlock.uId;
    } else if (viewPart === ViewPart.SORTING_CONTENT) {
      if (window.innerWidth <= 991 && window.innerWidth >= 480) {
        this.fs.sizeBlock = specialBlock.styles.maxWidth991Px?.basic['width'];
      } else if (window.innerWidth <= 479) {
        this.fs.sizeBlock = specialBlock.styles.maxWidth479Px?.basic['width'];
      } else {
        this.fs.sizeBlock = specialBlock.styles.all?.basic['width'];
      }
    } else if (viewPart === ViewPart.SORTING_TITLE) {
      const value = specialBlock.styles.all.basic.marginBottom;
      const newValue = parseInt(value) + 50 + 'px';
      this.fs.marginBottom = newValue;
    }
  }

  openOptions() {
    const element = document.querySelector(
      `.${this.globalService.specialBlockUId[this.keyBlock]}`,
    ) as HTMLElement;
    if (element) {
      if (
        !element.style.display ||
        element.style.display == '' ||
        element.style.display == 'none'
      ) {
        setTimeout(() => {
          element.style.display = 'block';
          const selector = `.${CSS.escape(
            this.globalService.specialBlockUId?.[this.keyBlock] ?? '',
          )}`;
          const nodes = document.querySelectorAll<HTMLElement>(selector);
          if (!nodes.length) return;

          nodes.forEach((node) => {
            node.style.setProperty('display', 'block', 'important');
          });
        }, 100);
      } else {
        this.closeList();
      }
    }
  }

  ngAfterViewInit(): void {
    this.replaceStylebloks(this.specialBlock.children[0]);
    this.subscriptions.push(
      this.globalService.mediaQuery.subscribe((mediaQuery) => {
        this.replaceStylebloks(this.specialBlock.children[0]);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.globalClickListener) {
      this.globalClickListener();
    }
  }

  replaceStylebloks(item: any) {
    if (item) {
      this.applyStyles(item, window.innerWidth);
      this.applyStylesToChildren(item.children);
    }
  }

  applyStylesToChildren(children: any[]) {
    if (children && children.length > 0) {
      children.forEach((child) => {
        this.applyStyles(child, window.innerWidth);
        this.applyStylesToChildren(child.children);
      });
    }
  }

  applyStyles(item: any, innerWidth: number) {
    const elementsMap = {
      sortingContent2: '.select-container',
      // 'sortingContent1': '.sortOptions',
      'sortingContent1-1': '.sortSelect',
      arrow_drop_down: '.sortIcon',
    };

    const elementSelector =
      elementsMap[item?.settings?.viewPart || item?.content];
    if (elementSelector) {
      const elementsHTML = document.querySelectorAll(elementSelector);
      this.applyResponsiveStyles(item, innerWidth, elementsHTML);
    }

    const viewPart = item?.settings?.viewPart;
    if (viewPart === ViewPart.SORTING_TITLE) {
      const element = document.querySelector(
        `.${item.children[0].uId}`,
      ) as HTMLElement;
      if (!element) {
        return;
      }
      const firstDiv = element.querySelector('div') as HTMLElement | null;
      if (!firstDiv) {
        return;
      }
      firstDiv.style.display = 'contents';
    }
  }

  applyResponsiveStyles(
    item: any,
    innerWidth: number,
    elementsHTML: NodeListOf<Element>,
  ) {
    let style = item.styles?.all?.basic;
    if (innerWidth <= 991 && innerWidth >= 480) {
      style = item.styles?.maxWidth991Px?.basic || style;
    } else if (innerWidth <= 479) {
      style = item.styles?.maxWidth479Px?.basic || style;
    }

    if (style) {
      for (const key in style) {
        if (key !== '')
          elementsHTML.forEach((element) => {
            this.renderer.setStyle(
              element,
              this.camelToKebabCase(key),
              style[key],
            );
          });
      }
    }
  }

  camelToKebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  loadSortOptions(): void {
    const backendData = this.sortingPositions;
    const view = this.viewName;
    let sortMappings = [];
    const sortOptionsByView = SORT_OPTIONS_BY_VIEW;

    if (sortOptionsByView[view]) {
      sortMappings = sortOptionsByView[view].filter(
        (option) => backendData[option.key],
      );
    }

    this.sortOptions = sortMappings.map((option) => ({
      label: option.label,
      value: option.value,
    }));
    this.fs.sortingParse = this.sortOptions;
  }

  onSelectChange(selectedOption): void {
    if (!selectedOption) {
      return;
    }
    this.fs.selectLastFilter = 'categories';
    this.fs.updateParams(true, FilterType.SORTING, selectedOption.value);
    // Etykietę ustawiamy od razu, żeby kliknięcie było widoczne, zanim wróci odpowiedź
    // listy; nawigacja z nowym adresem podstawi zaraz tę samą wartość. Poza adresem nic
    // jej już nie utrwala — wcześniejszy zapis do `localStorage` przeżywał całą sesję
    // i pokazywał wybór, którego lista dawno nie stosowała.
    this.globalService.optionSelect[this.viewName] = selectedOption.label;
    this.closeList();
  }

  closeList() {
    const element = document.querySelector(
      `.${this.globalService.specialBlockUId[this.keyBlock]}`,
    ) as HTMLElement;
    if (element) {
      element.style.display = 'none';
    }
  }

  isSingleChild(): boolean {
    return (
      this.specialBlock?.children?.length === 1 &&
      this.typBlock === 0 &&
      this.sortOptions.length > 0
    );
  }
}
